import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  ChevronRight,
  Calendar,
  Edit,
  RotateCcw,
  Shield,
  Users,
  Key,
  Settings,
  CheckSquare
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import roleService from '../../services/roleService'
import useToast from '../../hooks/useToast'
// Consolidated CSS is imported globally'

const RoleMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { error } = useToast()
  
  // Get the role data from navigation state or URL params
  const roleData = location.state?.roleData || {
    id: 1,
    roleCode: 'R001',
    roleName: 'Super Admin',
    displayRoleName: 'Super Admin',
    description: 'Full system access with all permissions',
    hierarchy: 1,
    status: 'Active',
    permissions: ['dashboard', 'sales_view', 'sales_create', 'sales_edit', 'sales_delete', 'crm_view', 'crm_create', 'crm_edit', 'contracts_view', 'contracts_create', 'projects_view', 'projects_create', 'timesheets_view', 'timesheets_create', 'expenses_view', 'expenses_create', 'procurement_view', 'procurement_create', 'subscriptions_view', 'assets_view', 'billing_view', 'settings_view', 'settings_edit', 'user_management', 'role_management'],
    createdDate: '2024-01-01',
    lastModified: '2024-01-15',
    redirectTo: './default/sales/home'
  }

  const [formData, setFormData] = useState({
    roleCode: '',
    roleName: '',
    displayRoleName: '',
    description: '',
    hierarchy: 1,
    status: 'Active',
    permissions: [],
    redirectTo: './default/sales/home',
    createdDate: '',
    lastModified: ''
  })
  const [loading, setLoading] = useState(true)

  // Available permissions list
  const availablePermissions = [
    { id: 'dashboard', name: 'Dashboard Access', category: 'General' },
    { id: 'sales_view', name: 'View Sales', category: 'Sales' },
    { id: 'sales_create', name: 'Create Sales', category: 'Sales' },
    { id: 'sales_edit', name: 'Edit Sales', category: 'Sales' },
    { id: 'sales_delete', name: 'Delete Sales', category: 'Sales' },
    { id: 'crm_view', name: 'View CRM', category: 'CRM' },
    { id: 'crm_create', name: 'Create CRM', category: 'CRM' },
    { id: 'crm_edit', name: 'Edit CRM', category: 'CRM' },
    { id: 'contracts_view', name: 'View Contracts', category: 'Contracts' },
    { id: 'contracts_create', name: 'Create Contracts', category: 'Contracts' },
    { id: 'projects_view', name: 'View Projects', category: 'Projects' },
    { id: 'projects_create', name: 'Create Projects', category: 'Projects' },
    { id: 'timesheets_view', name: 'View Timesheets', category: 'Timesheets' },
    { id: 'timesheets_create', name: 'Create Timesheets', category: 'Timesheets' },
    { id: 'expenses_view', name: 'View Expenses', category: 'Expenses' },
    { id: 'expenses_create', name: 'Create Expenses', category: 'Expenses' },
    { id: 'procurement_view', name: 'View Procurement', category: 'Procurement' },
    { id: 'procurement_create', name: 'Create Procurement', category: 'Procurement' },
    { id: 'subscriptions_view', name: 'View Subscriptions', category: 'Subscriptions' },
    { id: 'assets_view', name: 'View Assets', category: 'Assets' },
    { id: 'billing_view', name: 'View Billing', category: 'Billing' },
    { id: 'settings_view', name: 'View Settings', category: 'Settings' },
    { id: 'settings_edit', name: 'Edit Settings', category: 'Settings' },
    { id: 'user_management', name: 'User Management', category: 'Security' },
    { id: 'role_management', name: 'Role Management', category: 'Security' }
  ]

  // Pre-fill form data when component mounts
  useEffect(() => {
    const loadRoleData = async () => {
      try {
        setLoading(true)
        
        if (roleData.id) {
          // Fetch fresh data from backend
          const response = await roleService.getRoleById(roleData.id)
          if (response.success) {
            const role = roleService.transformRoleForDisplay(response.data)
            setFormData({
              roleCode: role.roleCode || '',
              roleName: role.roleName || '',
              displayRoleName: role.displayRoleName || '',
              description: role.description || '',
              hierarchy: role.hierarchy || 1,
              status: role.status || 'Active',
              permissions: role.permissions || [],
              redirectTo: role.redirectTo || './default/sales/home',
              createdDate: role.createdDate || '',
              lastModified: role.lastModified || ''
            })
          }
        } else {
          // Use passed data if no ID
          setFormData({
            roleCode: roleData.roleCode || '',
            roleName: roleData.roleName || '',
            displayRoleName: roleData.displayRoleName || '',
            description: roleData.description || '',
            hierarchy: roleData.hierarchy || 1,
            status: roleData.status || 'Active',
            permissions: roleData.permissions || [],
            redirectTo: roleData.redirectTo || './default/sales/home',
            createdDate: roleData.createdDate || '',
            lastModified: roleData.lastModified || ''
          })
        }
      } catch (err) {
        error('Failed to load role data: ' + err.message)
        // Fallback to passed data
        setFormData({
          roleCode: roleData.roleCode || '',
          roleName: roleData.roleName || '',
          displayRoleName: roleData.displayRoleName || '',
          description: roleData.description || '',
          hierarchy: roleData.hierarchy || 1,
          status: roleData.status || 'Active',
          permissions: roleData.permissions || [],
          redirectTo: roleData.redirectTo || './default/sales/home',
          createdDate: roleData.createdDate || '',
          lastModified: roleData.lastModified || ''
        })
      } finally {
        setLoading(false)
      }
    }

    loadRoleData()
  }, [roleData.id])

  const handleBack = () => {
    navigate('/settings/role-master')
  }

  const handleEdit = () => {
    navigate('/settings/role-master/edit', { state: { roleData } })
  }

  // Group permissions by category
  const groupedPermissions = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = []
    }
    acc[permission.category].push(permission)
    return acc
  }, {})

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Breadcrumb Navigation */}
        <div className="breadcrumb-section">
          <nav className="breadcrumb-nav">
            <button className="breadcrumb-item" onClick={() => navigate('/settings')}>
              <span>Settings</span>
            </button>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <button className="breadcrumb-item" onClick={() => navigate('/settings?tab=masters')}>
              <span>Masters</span>
            </button>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-current">Role Master</span>
          </nav>
        </div>

        {/* Header Section */}
        <div className="page-header">
          <div className="header-left">
            <button className="back-btn" onClick={handleBack}>
              <ArrowLeft size={20} />
            </button>
            <div className="page-title">
              <h1>Role Master - View</h1>
              <p>View role details and permissions</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-primary" onClick={handleEdit}>
              <Edit size={18} />
              Edit
            </button>
          </div>
        </div>

        {/* Form Section */}
        <div className="card">
          <div className="form-header-dark">
            <h2>Role Master - View</h2>
          </div>
          <div className="card-body">
            <form className="form-content">
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="roleCode" className="form-label required">
                    Role Code
                  </label>
                  <input
                    type="text"
                    id="roleCode"
                    name="roleCode"
                    value={formData.roleCode}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter role code"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="roleName" className="form-label required">
                    Role Name
                  </label>
                  <input
                    type="text"
                    id="roleName"
                    name="roleName"
                    value={formData.roleName}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter role name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="hierarchy" className="form-label required">
                    Hierarchy Level
                  </label>
                  <input
                    type="text"
                    id="hierarchy"
                    name="hierarchy"
                    value={`${formData.hierarchy} - ${formData.hierarchy === 1 ? 'Highest' : formData.hierarchy === 2 ? 'High' : formData.hierarchy === 3 ? 'Medium' : formData.hierarchy === 4 ? 'Low' : 'Lowest'}`}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select hierarchy level"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <input
                    type="text"
                    id="status"
                    name="status"
                    value={formData.status}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select status"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="createdDate" className="form-label required">
                    Created Date
                  </label>
                  <input
                    type="text"
                    id="createdDate"
                    name="createdDate"
                    value={formData.createdDate}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select created date"
                  />
                </div>
              </div>

              {/* Description Section */}
              <div className="form-group">
                <label htmlFor="description" className="form-label required">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  readOnly
                  className="form-textarea readonly-input"
                  placeholder="Enter role description"
                  rows={4}
                />
              </div>

              {/* Permissions Section */}
              <div className="form-group">
                <label className="form-label required">
                  Permissions ({formData.permissions.length} selected)
                </label>
                <div className="permissions-container">
                  {Object.entries(groupedPermissions).map(([category, permissions]) => (
                    <div key={category} className="permission-category">
                      <h4 className="permission-category-title">{category}</h4>
                      <div className="permission-list">
                        {permissions.map(permission => (
                          <div key={permission.id} className="permission-item">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              readOnly
                              className="permission-checkbox"
                            />
                            <span className="permission-name">{permission.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleMasterView
